import { useState } from "react";
import { useSelector } from "react-redux";

import { Decimal } from "classes/DecimalClasses";

import { QuickAddItem } from "components/QuickAddItem";
import { LineTableCell } from "components/formFields/LineTableCell";
import { LineClass } from "components/formFields/line/LineClass";
import { LineDescription } from "components/formFields/line/LineDescription";
import { LineInventoryItemBin } from "components/formFields/line/LineInventoryItemBin";
import { LineJobWorkcenter } from "components/formFields/line/LineJobWorkcenter";
import { LineLinkedTransaction } from "components/formFields/line/LineLinkedTransaction";
import { LineLotWithExpiration } from "components/formFields/line/LineLotWithExpiration";
import { LineOnHand } from "components/formFields/line/LineOnHand";
import { LineOnShortOrder } from "components/formFields/line/LineOnShortOrder";
import { LineQuantity } from "components/formFields/line/LineQuantity";
import { LineSerialsEnter } from "components/formFields/line/LineSerialsEnter";
import { LineUom } from "components/formFields/line/LineUom";

import { i18n } from "services/i18nService";
import { afterTouchOutputLine as afterTouchLine } from "services/sosInventoryService/build/afterTouchOutputLine";
import { OUTPUT_ITEM_CALCULATED_FIELDS } from "services/sosInventoryService/build/schema";
import {
  getItemRecord,
  clearItemDataFromLine,
} from "services/sosInventoryService/domainLogic";
import { updateLineWithItem } from "services/sosInventoryService/productionTransaction/domainLogic";
import { setPageDirty } from "services/utility/edit";
import { isLineFieldInError } from "services/utility/errors";
import { isFinishedGoods } from "services/utility/jobWorkCenter";
import { TYPES } from "services/utility/schema";

import { usePlans } from "hooks/usePlans";

import { OBJECT_TYPES } from "appConstants";
import { FINISHED_GOODS } from "appConstants";
import { OUTPUT_LINE_ITEM_FIELDS } from "editConfig";

const OBJECT_TYPE = OBJECT_TYPES.BUILD.fullString;

// created this wrapper to make drag and drop code easier to see
export function LineItem(props) {
  return <LineItemDetails {...props} />;
}

function LineItemDetails(props) {
  const {
    objectType,
    record,
    line,
    items,
    itemsCount,
    classes,
    classesCount,
    lineHandler,
    bins,
    location,
    jobWorkcenters,
    addItem,
    lineItemsErrors: errors,
    lineReadOnly = false,
  } = props;
  const { date, workcenter, autoSerialLotNumbering } = record;

  const [addNewItem, setAddNewItem] = useState(false);
  const [newItemName, setNewItemName] = useState("");
  const [itemLoading, setItemLoading] = useState(false);
  const { hasAtLeastPlusPlan } = usePlans();

  // SETTINGS

  const showQuantityOnBuildsAndShipments = useSelector(
    (state) =>
      state.userCompanySettings.settings.showQuantityOnBuildsAndShipments
  );

  function isInError(field) {
    return isLineFieldInError(field, line.lineNumber, errors);
  }

  function handleValueChange(fieldName, newValue) {
    let newLine;
    const lineItemFields = OUTPUT_LINE_ITEM_FIELDS[objectType];
    if (lineItemFields[TYPES.decimal].includes(fieldName)) {
      newLine = { ...line, [fieldName]: new Decimal(newValue || 0) };
    } else {
      newLine = { ...line, [fieldName]: newValue };
    }
    setPageDirty();
    lineHandler({
      type: "update",
      updatedLine: afterTouchLine(newLine, fieldName, record),
    });
  }

  function handleUomChange(_, uom) {
    setPageDirty();
    const oldUom = line.uom;
    const updatedLine = afterTouchLine({ ...line, uom, oldUom }, "uom");
    lineHandler({ type: "update", updatedLine });
  }

  async function handleItemChange(newItem, userInput) {
    if (!newItem) {
      const updatedLine = clearItemDataFromLine(line);
      setPageDirty();
      lineHandler({ type: "update", updatedLine });
      return;
    }
    if (newItem?.id === "add") {
      return openAddItem(userInput);
    }
    setItemLoading(true);
    const itemFields = showQuantityOnBuildsAndShipments
      ? ["onhand", ...OUTPUT_ITEM_CALCULATED_FIELDS]
      : OUTPUT_ITEM_CALCULATED_FIELDS;
    const item = await getItemRecord(
      newItem.id,
      record.location?.id,
      date,
      itemFields
    );
    const newLine = await updateLineWithItem(item, line, record, "output");
    setPageDirty();
    lineHandler({
      type: "update",
      updatedLine: afterTouchLine(newLine, "item"),
    });
    setItemLoading(false);
  }

  async function onAddItem(item) {
    setAddNewItem(false);
    await handleItemChange(item);
    addItem();
  }

  function openAddItem(userInput) {
    // handle "Add new item" in Item select input field; this does not change the
    // line item, so no need to proceed further into the handler
    setNewItemName(userInput);
    setAddNewItem(true);
  }

  if (!line) {
    return null;
  }

  return (
    <>
      <LineInventoryItemBin
        itemLoading={itemLoading}
        bins={bins}
        location={location}
        showBin={record.workcenter?.id === FINISHED_GOODS.id}
        handleItemChange={handleItemChange}
        handleReferenceChange={handleValueChange}
        objectType={OBJECT_TYPE}
        line={line}
        items={items}
        itemsCount={itemsCount}
        addItem={addItem}
        isInError={isInError}
        lineReadOnly={lineReadOnly}
      />
      <LineTableCell sx={{ minWidth: "24rem" }}>
        <LineDescription
          value={line.description}
          onValueChange={handleValueChange}
          error={isInError("description")}
          lineReadOnly={lineReadOnly}
        />
        {isFinishedGoods(workcenter) &&
          !autoSerialLotNumbering &&
          hasAtLeastPlusPlan && (
            <>
              {line.itemDetails?.serialTracking && (
                <LineSerialsEnter
                  value={line.serials}
                  onValueChange={handleValueChange}
                  quantity={line.quantity}
                  error={isInError("serials")}
                  lineReadOnly={lineReadOnly}
                />
              )}
              {line.itemDetails?.lotTracking && (
                <LineLotWithExpiration
                  lot={line.lot}
                  lotExpiration={line.lotExpiration}
                  onValueChange={handleValueChange}
                  itemLotTracked={line.itemDetails?.lotTracking}
                  quantity={line.quantity}
                  lotError={isInError("lot")}
                  lotExpirationError={isInError("lotExpiration")}
                  lineReadOnly={lineReadOnly}
                />
              )}
            </>
          )}
      </LineTableCell>
      {showQuantityOnBuildsAndShipments && (
        <LineOnHand value={line.onhand} lineReadOnly={lineReadOnly} />
      )}
      <LineOnShortOrder value={line.onSO} lineReadOnly={lineReadOnly} />
      <LineQuantity
        objectType={OBJECT_TYPE}
        value={line.quantity}
        onValueChange={handleValueChange}
        error={isInError("quantity")}
        disabled={itemLoading}
        lineReadOnly={lineReadOnly}
      />
      <LineUom
        value={line.uom}
        onValueChange={handleUomChange}
        itemUoms={line.itemDetails?.itemUoms}
        error={isInError("uom")}
        lineReadOnly={lineReadOnly}
      />
      <LineLinkedTransaction
        objectType={OBJECT_TYPE}
        transactionLabel={i18n("workOrder.labelAbbreviatedNumber")}
        value={line.linkedTransaction}
        onValueChange={handleValueChange}
        error={isInError("linkedTransaction")}
        lineReadOnly={lineReadOnly}
      />

      <LineClass
        value={line.class}
        classes={classes}
        classesCount={classesCount}
        onValueChange={handleValueChange}
        lineReadOnly={lineReadOnly}
      />
      <LineJobWorkcenter
        job={line.job}
        workcenter={line.workcenter}
        value={line.jobWorkcenter}
        jobWorkcenters={jobWorkcenters}
        onValueChange={handleValueChange}
        lineReadOnly={lineReadOnly}
      />
      {addNewItem && (
        <QuickAddItem
          open={addNewItem}
          initialNameValue={newItemName}
          onClose={() => setAddNewItem(false)}
          onAdd={onAddItem}
        />
      )}
    </>
  );
}
